import React, { useEffect, useRef, useState } from 'react';

import { loadGoogleMaps } from '@/lib/googleMaps';
import { GSM_MAP_STYLE } from '@/lib/googleMapsStyle';

export type PickTarget = 'origin' | 'destination';

interface Point {
  lat: number;
  lng: number;
}

interface AddressPickerMapProps {
  origin: Point | null;
  destination: Point | null;
  /** Which marker a map click drops/moves. Controlled by the parent toggle. */
  active: PickTarget;
  onActiveChange: (t: PickTarget) => void;
  onOriginChange: (lat: number, lng: number) => void;
  onDestinationChange: (lat: number, lng: number) => void;
  height?: number;
}

const ORIGIN_COLOR = '#5E2AAC';
const DEST_COLOR = '#2563eb';
const JAKARTA = { lat: -6.1754, lng: 106.8272 };
const round6 = (n: number) => Number(n.toFixed(6));

/**
 * Interactive dual-marker picker. Click the map to drop/move the *active*
 * marker (origin or destination); drag either marker to fine-tune. Emits
 * rounded lat/lng up to the form. Read-only when the SDK fails to load.
 */
const AddressPickerMap: React.FC<AddressPickerMapProps> = ({
  origin,
  destination,
  active,
  onActiveChange,
  onOriginChange,
  onDestinationChange,
  height = 300,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const originMarkerRef = useRef<any>(null);
  const destMarkerRef = useRef<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  // Latest callbacks/active for the map-click listener (bound once).
  const activeRef = useRef(active);
  const onOriginRef = useRef(onOriginChange);
  const onDestRef = useRef(onDestinationChange);
  useEffect(() => {
    activeRef.current = active;
    onOriginRef.current = onOriginChange;
    onDestRef.current = onDestinationChange;
  }, [active, onOriginChange, onDestinationChange]);

  // ── init map once ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !containerRef.current) return;
        const map = new maps.Map(containerRef.current, {
          center: origin ?? destination ?? JAKARTA,
          zoom: origin || destination ? 12 : 11,
          styles: GSM_MAP_STYLE,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'cooperative',
          clickableIcons: false,
        });
        mapRef.current = map;
        map.addListener('click', (e: any) => {
          const lat = round6(e.latLng.lat());
          const lng = round6(e.latLng.lng());
          if (activeRef.current === 'origin') onOriginRef.current(lat, lng);
          else onDestRef.current(lat, lng);
        });
        setLoading(false);
        setReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load Google Maps'
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── sync markers with props ────────────────────────────────────────────
  useEffect(() => {
    if (!ready) return;
    const w = window as any;
    const maps = w.google?.maps;
    const map = mapRef.current;
    if (!maps || !map) return;

    const upsert = (
      ref: React.MutableRefObject<any>,
      point: Point | null,
      color: string,
      label: string,
      onChange: (lat: number, lng: number) => void
    ) => {
      if (!point) {
        if (ref.current) {
          ref.current.setMap(null);
          ref.current = null;
        }
        return;
      }
      if (!ref.current) {
        ref.current = new maps.Marker({
          position: point,
          map,
          draggable: true,
          label: { text: label, color: '#fff', fontSize: '11px', fontWeight: '700' },
          icon: {
            path: maps.SymbolPath.CIRCLE,
            scale: 11,
            fillColor: color,
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
        });
        ref.current.addListener('dragend', (e: any) => {
          onChange(round6(e.latLng.lat()), round6(e.latLng.lng()));
        });
      } else {
        ref.current.setPosition(point);
      }
    };

    upsert(originMarkerRef, origin, ORIGIN_COLOR, 'O', (la, ln) =>
      onOriginRef.current(la, ln)
    );
    upsert(destMarkerRef, destination, DEST_COLOR, 'D', (la, ln) =>
      onDestRef.current(la, ln)
    );

    // Fit both when present, else center the one we have.
    if (origin && destination) {
      const bounds = new maps.LatLngBounds();
      bounds.extend(origin);
      bounds.extend(destination);
      map.fitBounds(bounds, 64);
    } else if (origin || destination) {
      map.setCenter((origin ?? destination) as Point);
    }
  }, [ready, origin, destination]);

  // ── cleanup ──────────────────────────────────────────────────────────────
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    return () => {
      originMarkerRef.current?.setMap(null);
      destMarkerRef.current?.setMap(null);
      mapRef.current = null;
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const toggleBtn = (t: PickTarget, color: string, text: string) => {
    const on = active === t;
    return (
      <button
        type="button"
        onClick={() => onActiveChange(t)}
        className={`inline-flex items-center gap-1.5 rounded-gsm border px-2.5 py-1 text-[11px] font-semibold tracking-tight transition-colors ${
          on
            ? 'border-primary bg-[rgba(94,42,172,0.06)] text-gray-900'
            : 'border-gray-300 text-gray-500 hover:text-gray-900'
        }`}
      >
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: color }}
        />
        {text}
      </button>
    );
  };

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
          Klik peta untuk set:
        </span>
        {toggleBtn('origin', ORIGIN_COLOR, 'Origin')}
        {toggleBtn('destination', DEST_COLOR, 'Destination')}
      </div>
      <div
        className="overflow-hidden rounded-gsm border border-gray-200 bg-gray-50"
        style={{ height }}
      >
        {error ? (
          <div className="flex h-full items-center justify-center px-3 text-center text-[12px] text-red-600">
            <i className="ri-error-warning-line mr-1 text-[14px]" />
            Peta tidak tersedia — koordinat manual di bawah tetap tersimpan.
          </div>
        ) : (
          <div className="relative h-full w-full">
            <div ref={containerRef} className="h-full w-full" />
            {loading && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/70 text-[12px] text-gray-500">
                Memuat peta…
              </div>
            )}
          </div>
        )}
      </div>
      <p className="mt-1.5 text-[11px] text-gray-400">
        Cari alamat di atas untuk auto-isi titik, atau klik/geser marker di peta.
      </p>
    </div>
  );
};

export default AddressPickerMap;
